import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogOut, User, Server } from 'lucide-react';
import { useTranslate, useTolgee } from '@tolgee/react';
import Logo from '../Logo';
import styles from './Navbar.module.css';
import { useRemoteConfig } from '../../context/RemoteConfigContext';
import { logEvent } from '../../services/analytics';

export default function Navbar() {
  const { currentUser, logout, isDemoMode } = useAuth();
  const { enableLanguages } = useRemoteConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslate();
  const tolgee = useTolgee();
  
  const isLoginPage = location.pathname === '/' || location.pathname === '/login';
  const currentLang = tolgee.getLanguage() || 'pl-PL';

  async function handleLogout() {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Błąd wylogowania:", error);
    }
  }

  return (
    <nav className={styles.nav}>
      {/* Logo */}
      <Link to="/" className={styles.logo}>
        <Logo size={44} iconOnly={true} />
      </Link>

      {/* Środkowe badże statusu bazy */}
      <div className={styles.statusContainer}>
        {isDemoMode && (
          <div className={styles.demoBadge}>
            <Server size={12} />
            {t('navbar.demo_mode', 'Tryb Demo (Local)')}
          </div>
        )}
      </div>

      {/* Profil / Nawigacja */}
      <div className={styles.actionsContainer}>
        {/* Przełącznik Języka */}
        {enableLanguages && (
          <div style={{
            display: 'flex',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid var(--glass-border)',
            borderRadius: '20px',
            padding: '2px',
            gap: '2px',
            alignItems: 'center'
          }}>
            <button
              onClick={() => {
                tolgee.changeLanguage('pl-PL');
                logEvent('change_language', { language: 'pl-PL' });
              }}
              style={{
                background: currentLang.startsWith('pl') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                border: 'none',
                borderRadius: '20px',
                color: currentLang.startsWith('pl') ? 'var(--text-light)' : 'var(--text-muted)',
                fontSize: '0.72rem',
                fontWeight: 700,
                padding: '4px 10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                outline: 'none'
              }}
              title="Polski"
            >
              <span>🇵🇱</span>
              <span style={{ opacity: currentLang.startsWith('pl') ? 1 : 0.7 }}>PL</span>
            </button>
            <button
              onClick={() => {
                tolgee.changeLanguage('en');
                logEvent('change_language', { language: 'en' });
              }}
              style={{
                background: currentLang === 'en' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                border: 'none',
                borderRadius: '20px',
                color: currentLang === 'en' ? 'var(--text-light)' : 'var(--text-muted)',
                fontSize: '0.72rem',
                fontWeight: 700,
                padding: '4px 10px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                outline: 'none'
              }}
              title="English"
            >
              <span>🇬🇧</span>
              <span style={{ opacity: currentLang === 'en' ? 1 : 0.7 }}>EN</span>
            </button>
          </div>
        )}

        {currentUser ? (
          <>
            <Link to="/dashboard" className={styles.navLink}>
              {t('navbar.dashboard', 'Panel Główny')}
            </Link>
            
            <div className={styles.userProfile}>
              <User size={14} className={styles.profileIcon} />
              <span className={styles.profileEmail}>
                {currentUser.email}
              </span>
            </div>

            <button 
              onClick={handleLogout} 
              className={`glass-button-secondary ${styles.logoutButton}`.trim()}
              title={t('navbar.logout', 'Wyloguj się')}
            >
              <LogOut size={16} />
            </button>
          </>
        ) : (
          !isLoginPage && (
            <div style={{ display: 'flex', gap: '12px' }}>
              <Link to="/" className={`glass-button ${styles.loginButton}`.trim()}>
                {t('navbar.login', 'Zaloguj')}
              </Link>
            </div>
          )
        )}
      </div>
    </nav>
  );
}
